import { useMemo } from "react";
import { Link } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { LineChart, BarChart, PieChart, SparkLineChart, Gauge, gaugeClasses } from "@mui/x-charts";
import { cls } from "../hatim/soft/SoftKit";
import { useGetDashboardQuery } from "../../api/adminApi";
import {
  formatNumber,
  formatTime,
  mergeDailySeries,
  shortDayLabel,
  roleLabel,
} from "../../lib/adminFormat";
import {
  PageHeader,
  Section,
  NoData,
  ErrorRetry,
  SkeletonLine,
  StatRow,
  useChartColors,
} from "./components/AdminKit";

const ROLE_ORDER = ["User", "Admin"];

/* ------------------------------------------------------------------ */
/* Metrik şeridi                                                       */
/* ------------------------------------------------------------------ */

function Metric({ Icon, label, value, hint, spark, sparkColor, to, tone }) {
  const warm = tone === "warm";
  const body = (
    <div className="h-full flex flex-col gap-2 p-4">
      <div className="flex items-center gap-2">
        <span
          className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
            warm ? "bg-terakkiWarning/15 text-[#9A6412] dark:bg-[#F2C46D]/15 dark:text-[#F2C46D]" : cls.avatar
          }`}
        >
          <Icon sx={{ fontSize: 18 }} aria-hidden />
        </span>
        <span className={`text-sm leading-tight ${cls.muted}`}>{label}</span>
      </div>
      <div className="flex items-end gap-2">
        <span className={`text-5xl font-Medium leading-none tabular-nums tracking-tight ${cls.title}`}>{value}</span>
        {spark && spark.length > 1 && (
          <div className="flex-1 min-w-0 h-8 -mb-1" aria-hidden>
            <SparkLineChart data={spark} height={32} curve="natural" area colors={[sparkColor]} margin={{ top: 4, bottom: 2, left: 2, right: 2 }} />
          </div>
        )}
      </div>
      {hint && <span className={`text-xs ${cls.faint}`}>{hint}</span>}
    </div>
  );
  const cell = `min-w-0 border-r border-b border-terakkiBorder dark:border-white/[0.14] ${
    warm ? "bg-terakkiWarning/[0.06] dark:bg-[#F2C46D]/[0.06]" : ""
  }`;
  if (to) {
    return (
      <Link to={to} className={`${cell} block transition-colors ${cls.rowHover}`}>
        {body}
      </Link>
    );
  }
  return <div className={cell}>{body}</div>;
}

function MetricStrip({ d, colors }) {
  const users = d.users;
  const activity = d.activity;
  const library = d.library;
  const muzakere = d.muzakere;
  const groups = d.groups;
  const dash = "·";

  const signupSpark = (users?.signupsDaily || []).slice(-14).map((x) => Number(x.count) || 0);
  const activeSpark = (activity?.dailyActive || []).slice(-14).map((x) => Number(x.count) || 0);
  const pending = Number(library?.pending) || 0;

  return (
    <div className={`${cls.panel} overflow-hidden`}>
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 -mr-px -mb-px">
        <Metric
          Icon={PeopleOutlineIcon}
          label="Toplam kullanıcı"
          value={users ? formatNumber(users.total) : dash}
          hint={users ? `Son 30 günde +${formatNumber(users.newLast30)}` : "Veri alınamadı"}
          spark={signupSpark}
          sparkColor={colors.muted}
        />
        <Metric
          Icon={BoltOutlinedIcon}
          label="Bugün aktif"
          value={activity ? formatNumber(activity.activeToday) : dash}
          hint={activity ? `7 gün ortalaması ${formatNumber(activity.avgDailyActive7)}` : "Veri alınamadı"}
          spark={activeSpark}
          sparkColor={colors.primary}
        />
        <Metric
          Icon={DateRangeOutlinedIcon}
          label="7 günde aktif"
          value={activity ? formatNumber(activity.activeLast7) : dash}
          hint={activity ? `30 günde ${formatNumber(activity.activeLast30)} kişi` : "Veri alınamadı"}
        />
        <Metric
          Icon={PendingActionsOutlinedIcon}
          label="Onay bekleyen paket"
          value={library ? formatNumber(pending) : dash}
          hint={library ? `Kütüphanede ${formatNumber(library.total)} paket` : "Veri alınamadı"}
          to="/sistem/kutuphane"
          tone={pending > 0 ? "warm" : undefined}
        />
        <Metric
          Icon={ForumOutlinedIcon}
          label="Müzakere"
          value={muzakere ? formatNumber(muzakere.total) : dash}
          hint={muzakere ? `Son 30 günde ${formatNumber(muzakere.last30)}` : "Veri alınamadı"}
        />
        <Metric
          Icon={GroupsOutlinedIcon}
          label="Grup"
          value={groups ? formatNumber(groups.total) : dash}
          hint={groups ? `Ortalama ${formatNumber(groups.avgMembers)} üye` : "Veri alınamadı"}
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Grafik bölümleri                                                    */
/* ------------------------------------------------------------------ */

function LegendDot({ color, label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm ${cls.muted}`}>
      <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} aria-hidden />
      {label}
    </span>
  );
}

function ActivityChart({ users, activity, colors }) {
  const rows = useMemo(
    () => mergeDailySeries(users?.signupsDaily, activity?.dailyActive),
    [users, activity]
  );
  const legend = (
    <div className="hidden sm:flex items-center gap-3">
      <LegendDot color={colors.primary} label="Günlük aktif" />
      <LegendDot color={colors.secondary} label="Yeni kayıt" />
    </div>
  );
  return (
    <Section
      title="Son 30 gün"
      subtitle="Günlük aktif kullanıcı ve yeni kayıtlar"
      right={legend}
      className="lg:col-span-2"
      bodyClassName="px-1 pt-2 pb-1"
    >
      {!users && !activity ? (
        <NoData />
      ) : rows.length === 0 ? (
        <NoData text="Bu dönemde veri yok" />
      ) : (
        <LineChart
          height={280}
          margin={{ top: 16, right: 16, bottom: 28, left: 40 }}
          xAxis={[
            {
              scaleType: "point",
              data: rows.map((r) => r.date),
              valueFormatter: (v) => shortDayLabel(v),
              tickInterval: (v, i) => i % 5 === 0 || i === rows.length - 1,
              tickLabelStyle: { fontSize: 11 },
            },
          ]}
          yAxis={[{ tickLabelStyle: { fontSize: 11 }, min: 0 }]}
          series={[
            {
              id: "active",
              label: "Günlük aktif",
              data: rows.map((r) => r.active),
              color: colors.primary,
              area: true,
              curve: "monotoneX",
              showMark: false,
            },
            {
              id: "signups",
              label: "Yeni kayıt",
              data: rows.map((r) => r.signups),
              color: colors.secondary,
              curve: "monotoneX",
              showMark: false,
            },
          ]}
          grid={{ horizontal: true }}
          slotProps={{ legend: { hidden: true } }}
          sx={{
            "& .MuiAreaElement-root": { fillOpacity: 0.14 },
            "& .MuiLineElement-root": { strokeWidth: 2.25 },
            "& .MuiChartsGrid-line": { stroke: colors.grid, strokeDasharray: "3 4" },
            "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: colors.grid },
          }}
        />
      )}
      {activity && (
        <div className={`flex flex-wrap gap-x-4 gap-y-1 px-3 pb-2 text-sm ${cls.muted}`}>
          <span>Son 7 günde uygulamayı açan (bildirimi açık): {formatNumber(activity.appOpenedLast7)}</span>
          {users && <span>· Son 7 günde yeni kayıt: {formatNumber(users.newLast7)}</span>}
        </div>
      )}
    </Section>
  );
}

function RolesDonut({ users, colors }) {
  const data = ROLE_ORDER.map((role, i) => ({
    id: role,
    label: roleLabel(role),
    value: Number(users?.byRole?.[role]) || 0,
    color: colors.roles[i],
  }));
  const total = data.reduce((s, x) => s + x.value, 0);
  return (
    <Section title="Rollere göre" subtitle="Etkin hesaplar" bodyClassName="p-0">
      {!users ? (
        <NoData />
      ) : total === 0 ? (
        <NoData text="Henüz kullanıcı yok" />
      ) : (
        <>
          <div className="relative h-[190px] mt-2">
            <PieChart
              height={190}
              margin={{ top: 4, bottom: 4, left: 4, right: 4 }}
              series={[
                {
                  data: data.filter((x) => x.value > 0),
                  innerRadius: 62,
                  outerRadius: 88,
                  paddingAngle: 2,
                  cornerRadius: 4,
                  valueFormatter: (item) => formatNumber(item.value),
                },
              ]}
              slotProps={{ legend: { hidden: true } }}
              sx={{ "& .MuiPieArc-root": { stroke: "none" } }}
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className={`text-4xl font-Medium tabular-nums ${cls.title}`}>{formatNumber(total)}</span>
              <span className={`text-xs ${cls.muted}`}>hesap</span>
            </div>
          </div>
          <ul className={`mt-2 ${cls.footer}`}>
            {data.map((x) => (
              <StatRow
                key={x.id}
                dot={x.color}
                label={x.label}
                value={`${formatNumber(x.value)}  ·  %${total ? Math.round((x.value / total) * 100) : 0}`}
              />
            ))}
            {users.deleted > 0 && <StatRow label="Kapatılmış hesap" value={formatNumber(users.deleted)} />}
          </ul>
        </>
      )}
    </Section>
  );
}

function LibraryChart({ library, colors }) {
  const bars = library
    ? [
        { label: "Onaylı", value: Number(library.verified) || 0, color: colors.tertiary },
        { label: "Bekleyen", value: Number(library.pending) || 0, color: colors.secondary },
        { label: "Tavsiyeli", value: Number(library.recommended) || 0, color: colors.primary },
        { label: "Gizli", value: Number(library.hidden) || 0, color: colors.danger },
      ]
    : [];
  return (
    <Section
      title="Kütüphane"
      subtitle={library ? `${formatNumber(library.total)} paket` : undefined}
      right={
        <Link to="/sistem/kutuphane" className={`text-sm ${cls.accent} hover:underline`}>
          Denetle
        </Link>
      }
      bodyClassName="p-0"
    >
      {!library ? (
        <NoData />
      ) : (
        <>
          <div className="px-1 pt-1">
            <BarChart
              height={180}
              layout="horizontal"
              margin={{ top: 8, right: 20, bottom: 24, left: 72 }}
              yAxis={[
                {
                  scaleType: "band",
                  data: bars.map((b) => b.label),
                  categoryGapRatio: 0.45,
                  colorMap: { type: "ordinal", colors: bars.map((b) => b.color) },
                  tickLabelStyle: { fontSize: 12 },
                },
              ]}
              xAxis={[{ min: 0, tickLabelStyle: { fontSize: 11 }, tickMinStep: 1 }]}
              series={[{ data: bars.map((b) => b.value), valueFormatter: (v) => formatNumber(v) }]}
              borderRadius={4}
              grid={{ vertical: true }}
              slotProps={{ legend: { hidden: true } }}
              sx={{
                "& .MuiChartsGrid-line": { stroke: colors.grid, strokeDasharray: "3 4" },
                "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: colors.grid },
              }}
            />
          </div>
          <ul className={cls.footer}>
            <StatRow label="Çatal" value={formatNumber(library.forks)} />
            <StatRow label="Yıldız" value={formatNumber(library.stars)} />
            <StatRow label="Kullanım" value={formatNumber(library.uses)} />
          </ul>
        </>
      )}
    </Section>
  );
}

function MuzakereSummary({ muzakere, colors }) {
  const pct = Math.max(0, Math.min(100, Number(muzakere?.avgScorePercent) || 0));
  return (
    <Section title="Müzakere ve değerlendirme" subtitle="Son 30 gün ve toplam" bodyClassName="p-0">
      {!muzakere ? (
        <NoData />
      ) : (
        <>
          <div className="flex items-center gap-4 px-4 pt-3 pb-1">
            <div className="w-[128px] h-[104px] shrink-0">
              <Gauge
                width={128}
                height={112}
                value={pct}
                startAngle={-110}
                endAngle={110}
                innerRadius="76%"
                outerRadius="100%"
                cornerRadius="50%"
                text={({ value }) => (muzakere.responses > 0 ? `%${value}` : "·")}
                sx={{
                  [`& .${gaugeClasses.valueArc}`]: { fill: colors.primary },
                  [`& .${gaugeClasses.referenceArc}`]: { fill: colors.grid },
                  [`& .${gaugeClasses.valueText} text`]: {
                    fill: colors.dark ? "#FFFFFF" : "#222831",
                    fontSize: 22,
                  },
                }}
              />
            </div>
            <div className="min-w-0">
              <div className={`text-base ${cls.title}`}>Ortalama başarı</div>
              <div className={`text-sm ${cls.muted}`}>
                {formatNumber(muzakere.responses)} değerlendirme yanıtına göre
              </div>
            </div>
          </div>
          <ul className={cls.footer}>
            <StatRow label="Toplam müzakere" value={formatNumber(muzakere.total)} hint={`Son 30 günde ${formatNumber(muzakere.last30)}`} />
            <StatRow label="Değerlendirmeli" value={formatNumber(muzakere.withAssessment)} />
            <StatRow label="Başlatılan değerlendirme" value={formatNumber(muzakere.assessmentsStarted)} />
          </ul>
        </>
      )}
    </Section>
  );
}

const PLATFORM_LABELS = { ios: "iOS", android: "Android", web: "Web" };

function DevicesSummary({ devices, notifications, hatim, colors }) {
  const platforms = devices
    ? Object.entries(devices.byPlatform || {})
        .map(([k, v]) => ({ key: k, label: PLATFORM_LABELS[k] || k, value: Number(v) || 0 }))
        .sort((a, b) => b.value - a.value)
    : [];
  const max = Math.max(1, ...platforms.map((p) => p.value));
  const barColors = [colors.primary, colors.secondary, colors.violet, colors.tertiary];
  return (
    <Section title="Cihazlar ve duyurular" subtitle={devices ? `Bildirim alan ${formatNumber(devices.active)} cihaz` : undefined} bodyClassName="p-0">
      {!devices ? (
        <NoData />
      ) : platforms.length === 0 ? (
        <NoData text="Kayıtlı cihaz yok" />
      ) : (
        <ul className="px-4 pt-3 pb-2 flex flex-col gap-3">
          {platforms.map((p, i) => (
            <li key={p.key}>
              <div className="flex items-baseline justify-between text-base">
                <span className={cls.muted}>{p.label}</span>
                <span className={`tabular-nums ${cls.title}`}>{formatNumber(p.value)}</span>
              </div>
              <div className={`mt-1.5 h-2 rounded-full overflow-hidden ${cls.track}`}>
                <div
                  className="h-full rounded-full"
                  style={{ width: `${(p.value / max) * 100}%`, background: barColors[i % barColors.length] }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
      <ul className={cls.footer}>
        {notifications ? (
          <>
            <StatRow label="Duyuru (30 gün)" value={formatNumber(notifications.last30)} />
            <StatRow label="Ulaşan bildirim (30 gün)" value={formatNumber(notifications.sentLast30)} />
          </>
        ) : (
          <StatRow label="Duyurular" value="Veri alınamadı" />
        )}
        {hatim ? (
          <StatRow
            label="Hatim"
            value={formatNumber(hatim.instances)}
            hint={`${formatNumber(hatim.templates)} şablon`}
          />
        ) : (
          <StatRow label="Hatim" value="Veri alınamadı" />
        )}
      </ul>
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Platformlar ve web sitesi ziyaretleri                               */
/* ------------------------------------------------------------------ */

const CLIENT_KEYS = ["web", "ios", "android"];

function platformColors(colors) {
  return { web: colors.primary, ios: colors.violet, android: colors.tertiary };
}

function PlatformsChart({ platforms, colors }) {
  const rows = platforms?.daily || [];
  const pc = platformColors(colors);
  const legend = (
    <div className="hidden sm:flex items-center gap-3">
      {CLIENT_KEYS.map((k) => (
        <LegendDot key={k} color={pc[k]} label={PLATFORM_LABELS[k]} />
      ))}
    </div>
  );
  return (
    <Section
      title="Platformlar"
      subtitle="Günlük giriş yapan kullanıcı, son 30 gün"
      right={legend}
      className="lg:col-span-2"
      bodyClassName="px-1 pt-2 pb-1"
    >
      {!platforms ? (
        <NoData />
      ) : rows.every((r) => !r.web && !r.ios && !r.android) ? (
        <NoData text="Henüz kayıt yok" />
      ) : (
        <BarChart
          height={240}
          margin={{ top: 12, right: 16, bottom: 28, left: 40 }}
          xAxis={[
            {
              scaleType: "band",
              data: rows.map((r) => r.date),
              valueFormatter: (v) => shortDayLabel(v),
              categoryGapRatio: 0.4,
              tickLabelStyle: { fontSize: 11 },
              tickInterval: (v, i) => i % 5 === 0 || i === rows.length - 1,
            },
          ]}
          yAxis={[{ min: 0, tickLabelStyle: { fontSize: 11 }, tickMinStep: 1 }]}
          series={CLIENT_KEYS.map((k) => ({
            id: k,
            label: PLATFORM_LABELS[k],
            data: rows.map((r) => Number(r[k]) || 0),
            stack: "all",
            color: pc[k],
          }))}
          grid={{ horizontal: true }}
          slotProps={{ legend: { hidden: true } }}
          sx={{
            "& .MuiChartsGrid-line": { stroke: colors.grid, strokeDasharray: "3 4" },
            "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: colors.grid },
          }}
        />
      )}
    </Section>
  );
}

function PlatformsSummary({ platforms, colors }) {
  const pc = platformColors(colors);
  const w7 = platforms?.last7;
  const w30 = platforms?.last30;
  const total7 = Number(w7?.total) || 0;
  return (
    <Section
      title="Kullanıcılar nereden giriyor"
      subtitle={platforms ? `Son 7 günde ${formatNumber(total7)} kişi` : undefined}
      bodyClassName="p-0"
    >
      {!platforms ? (
        <NoData />
      ) : (
        <>
          <ul className="px-4 pt-3 pb-2 flex flex-col gap-3">
            {CLIENT_KEYS.map((k) => {
              const value = Number(w7?.[k]) || 0;
              const pct = total7 ? Math.round((value / total7) * 100) : 0;
              return (
                <li key={k}>
                  <div className="flex items-baseline justify-between text-base">
                    <span className={cls.muted}>{PLATFORM_LABELS[k]}</span>
                    <span className={`tabular-nums ${cls.title}`}>
                      {formatNumber(value)} <span className={`text-sm ${cls.faint}`}>· %{pct}</span>
                    </span>
                  </div>
                  <div className={`mt-1.5 h-2 rounded-full overflow-hidden ${cls.track}`}>
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pc[k] }} />
                  </div>
                </li>
              );
            })}
          </ul>
          <ul className={cls.footer}>
            <StatRow label="Hem web hem mobil (7 gün)" value={formatNumber(w7?.webAndMobile)} />
            <StatRow
              label="Son 30 gün"
              value={formatNumber(w30?.total)}
              hint={`Web ${formatNumber(w30?.web)} · iOS ${formatNumber(w30?.ios)} · Android ${formatNumber(w30?.android)}`}
            />
            <StatRow
              label="Bugün"
              value={formatNumber(platforms.today?.total)}
              hint={`Web ${formatNumber(platforms.today?.web)} · iOS ${formatNumber(platforms.today?.ios)} · Android ${formatNumber(platforms.today?.android)}`}
            />
          </ul>
        </>
      )}
    </Section>
  );
}

function VisitsSummary({ visits, colors }) {
  const spark = (visits?.daily || []).slice(-14).map((x) => Number(x.views) || 0);
  return (
    <Section title="Web sitesi ziyareti" subtitle="Tanıtım sayfaları, anonim" bodyClassName="p-0">
      {!visits ? (
        <NoData />
      ) : (
        <>
          <div className="flex items-end gap-3 px-4 pt-3 pb-2">
            <div className="shrink-0">
              <div className={`text-4xl font-Medium leading-none tabular-nums ${cls.title}`}>
                {formatNumber(visits.last7?.views)}
              </div>
              <div className={`text-sm mt-1 ${cls.muted}`}>görüntüleme, 7 gün</div>
            </div>
            {spark.length > 1 && (
              <div className="flex-1 min-w-0 h-12" aria-hidden>
                <SparkLineChart data={spark} height={48} curve="natural" area colors={[colors.primary]} margin={{ top: 4, bottom: 2, left: 2, right: 2 }} />
              </div>
            )}
          </div>
          <ul className={cls.footer}>
            <StatRow label="Bugün" value={formatNumber(visits.today?.views)} hint={`${formatNumber(visits.today?.visitors)} ziyaretçi`} />
            <StatRow label="Son 7 gün" value={formatNumber(visits.last7?.views)} hint={`${formatNumber(visits.last7?.visitors)} günlük tekil ziyaretçi`} />
            <StatRow label="Son 30 gün" value={formatNumber(visits.last30?.views)} hint={`${formatNumber(visits.last30?.visitors)} günlük tekil ziyaretçi`} />
          </ul>
        </>
      )}
    </Section>
  );
}

function SecurityChart({ security, colors }) {
  const rows = security?.eventsDaily || [];
  const legend = (
    <div className="hidden sm:flex items-center gap-3">
      <LegendDot color={colors.danger} label="Güvenlik" />
      <LegendDot color={colors.muted} label="İşlem" />
    </div>
  );
  return (
    <Section
      title="Denetim olayları"
      subtitle="Son 14 gün"
      right={legend}
      className="lg:col-span-2"
      bodyClassName="px-1 pt-2 pb-1"
    >
      {!security ? (
        <NoData />
      ) : rows.length === 0 ? (
        <NoData text="Bu dönemde olay yok" />
      ) : (
        <BarChart
          height={240}
          margin={{ top: 12, right: 16, bottom: 28, left: 40 }}
          xAxis={[
            {
              scaleType: "band",
              data: rows.map((r) => r.date),
              valueFormatter: (v) => shortDayLabel(v),
              categoryGapRatio: 0.45,
              tickLabelStyle: { fontSize: 11 },
              tickInterval: (v, i) => i % 2 === 0,
            },
          ]}
          yAxis={[{ min: 0, tickLabelStyle: { fontSize: 11 }, tickMinStep: 1 }]}
          series={[
            { id: "security", label: "Güvenlik", data: rows.map((r) => Number(r.security) || 0), stack: "all", color: colors.danger },
            { id: "mutation", label: "İşlem", data: rows.map((r) => Number(r.mutation) || 0), stack: "all", color: colors.muted },
          ]}
          grid={{ horizontal: true }}
          slotProps={{ legend: { hidden: true } }}
          sx={{
            "& .MuiChartsGrid-line": { stroke: colors.grid, strokeDasharray: "3 4" },
            "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: colors.grid },
          }}
        />
      )}
    </Section>
  );
}

function SecuritySummary({ security, groups }) {
  return (
    <Section
      title="Güvenlik, son 7 gün"
      right={
        <Link to="/sistem/kayitlar" className={`text-sm ${cls.accent} hover:underline`}>
          Kayıtlar
        </Link>
      }
      bodyClassName="p-0"
    >
      {!security ? (
        <NoData />
      ) : (
        <ul>
          <StatRow label="Hatalı giriş denemesi" value={formatNumber(security.loginFailuresLast7)} />
          <StatRow label="Hesap kilitlenmesi" value={formatNumber(security.lockoutsLast7)} />
          <StatRow label="Reddedilen erişim" value={formatNumber(security.forbiddenLast7)} hint="Geçersiz oturum, yetki ya da imza" />
        </ul>
      )}
      {groups && (
        <ul className={cls.footer}>
          <StatRow label="Grup" value={formatNumber(groups.total)} />
          <StatRow label="Müzakere yapan grup (30 gün)" value={formatNumber(groups.withMuzakereLast30)} />
          <StatRow label="Grup başına üye" value={formatNumber(groups.avgMembers)} />
        </ul>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Yükleniyor iskeleti                                                 */
/* ------------------------------------------------------------------ */

function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label="Yükleniyor">
      <div className={`${cls.panel} overflow-hidden`}>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 -mr-px -mb-px">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="p-4 flex flex-col gap-3 border-r border-b border-terakkiBorder dark:border-white/[0.14]">
              <SkeletonLine className="h-4 w-2/3" />
              <SkeletonLine className="h-8 w-1/2" />
              <SkeletonLine className="h-3 w-3/4" />
            </div>
          ))}
        </div>
      </div>
      {[0, 1].map((row) => (
        <div key={row} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className={`${cls.panel} lg:col-span-2 p-4 flex flex-col gap-3`}>
            <SkeletonLine className="h-4 w-40" />
            <SkeletonLine className="h-[240px] w-full" />
          </div>
          <div className={`${cls.panel} p-4 flex flex-col gap-3`}>
            <SkeletonLine className="h-4 w-32" />
            <SkeletonLine className="h-[240px] w-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sayfa                                                               */
/* ------------------------------------------------------------------ */

export default function SystemDashboardPage() {
  const { data, isLoading, isFetching, isError, error, refetch, fulfilledTimeStamp } = useGetDashboardQuery(
    undefined,
    { refetchOnMountOrArgChange: 60 }
  );
  const colors = useChartColors();

  const updatedAt = data?.generatedAt || (fulfilledTimeStamp ? new Date(fulfilledTimeStamp).toISOString() : null);

  const headerRight = (
    <>
      {updatedAt && (
        <span className={`text-sm ${cls.muted}`}>Son güncelleme {formatTime(updatedAt)}</span>
      )}
      <button
        type="button"
        onClick={refetch}
        disabled={isFetching}
        aria-label="Yenile"
        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-terakkiBorder text-sm text-terakkiPrimaryDark hover:bg-terakkiBrandCloud disabled:opacity-60 dark:border-white/[0.14] dark:text-[#8FC9CA] dark:hover:bg-white/[0.08]"
      >
        <RefreshIcon sx={{ fontSize: 18 }} className={isFetching ? "animate-spin" : ""} aria-hidden />
        Yenile
      </button>
    </>
  );

  let content;
  if (isLoading) {
    content = <DashboardSkeleton />;
  } else if (isError || !data) {
    content = (
      <div className={cls.panel}>
        <ErrorRetry message={error?.message || "Pano bilgileri alınamadı."} onRetry={refetch} error={error} />
      </div>
    );
  } else {
    content = (
      <div className={`flex flex-col gap-4 transition-opacity ${isFetching ? "opacity-70" : ""}`}>
        <MetricStrip d={data} colors={colors} />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ActivityChart users={data.users} activity={data.activity} colors={colors} />
          <RolesDonut users={data.users} colors={colors} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <PlatformsChart platforms={data.platforms} colors={colors} />
          <PlatformsSummary platforms={data.platforms} colors={colors} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <LibraryChart library={data.library} colors={colors} />
          <MuzakereSummary muzakere={data.muzakere} colors={colors} />
          <DevicesSummary devices={data.devices} notifications={data.notifications} hatim={data.hatim} colors={colors} />
          <VisitsSummary visits={data.visits} colors={colors} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SecurityChart security={data.security} colors={colors} />
          <SecuritySummary security={data.security} groups={data.groups} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto flex flex-col gap-5 pb-8">
      <PageHeader title="Sistem Panosu" subtitle="Terakki genel görünümü" right={headerRight} />
      {content}
    </div>
  );
}
